import { z } from "zod";
import { BEREICH_SCHLUESSEL, CUSTOMER_LOCALES, LOCALES, ROLES } from "./constants.js";
/**
 * Ein Schema, beide Seiten. Das Frontend prueft damit das Formular, das
 * Backend prueft damit die Anfrage. Keine doppelte Pflege, keine Luecke.
 */
export const uuidSchema = z.string().uuid();
export const localeSchema = z.enum(LOCALES);
export const roleSchema = z.enum(ROLES);
/** Mehrsprachiger Text, wie er in der Datenbank als JSONB liegt. */
export const i18nTextSchema = z.record(localeSchema, z.string()).refine((value) => Object.keys(value).length > 0, { message: "mindestens eine Sprache erforderlich" });
export const passwordSchema = z
    .string()
    .min(12, "mindestens 12 Zeichen")
    .max(200)
    .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /[0-9]/.test(v), {
    message: "mindestens ein Klein-, ein Grossbuchstabe und eine Ziffer"
});
export const loginSchema = z.object({
    email: z.string().trim().email().max(320),
    password: z.string().min(1).max(200),
    deviceLabel: z.string().max(120).optional()
});
export const acceptInvitationSchema = z.object({
    token: z.string().min(20).max(200),
    password: passwordSchema,
    deviceLabel: z.string().max(120).optional()
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema
});
export const createInvitationSchema = z.object({
    email: z.string().trim().email().max(320),
    displayName: z.string().min(2).max(120),
    role: roleSchema,
    locale: localeSchema.default("de"),
    /*
     * Der Vertrag steht schon in der Einladung.
     *
     * Ohne Wochenstunden hat ein Konto kein Soll, und ohne Soll gibt es keine
     * Ueberstunden und keine Sollspalte im Auszug fuer die Gestoria. Das faellt
     * erst auf, wenn jemand nach seinen Stunden fragt. Hier gesetzt, kann es
     * nicht mehr vergessen werden.
     */
    wochenstunden: z.number().min(0).max(80).nullish(),
    eintritt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum im Format JJJJ-MM-TT").nullish()
});
/**
 * Ein leeres Formularfeld schickt der Browser als leeren Text, nicht als
 * "nicht gesetzt". Ohne diese Umwandlung scheitert jede Pruefung auf E-Mail
 * oder Datum, sobald jemand ein freiwilliges Feld leer laesst. Die Umwandlung
 * gehoert an genau eine Stelle, nicht in jedes Formular.
 */
const leerAlsFehlend = (schema) => z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? undefined : v), schema.optional());
const text = (max) => leerAlsFehlend(z.string().trim().max(max));
/**
 * Ein Feld mit einer oder mehreren E-Mail-Adressen.
 *
 * Ein Haus gehoert oft zweien. "clauweg@web.de;jocurth@gmx.de" ist am Kunden
 * genau das, was das Buero meint, und es steht dort auch so: der Bericht soll
 * an beide gehen. Ein Feld, das nur eine Adresse zulaesst, zwingt zu der
 * Entscheidung, welchem der beiden Eigentuemer man nichts schickt.
 *
 * Getrennt wird mit Semikolon oder Komma, so wie es jedes Mailprogramm kann.
 * Gespeichert wird einheitlich mit Semikolon und Leerzeichen, damit im Feld
 * spaeter nicht drei Schreibweisen nebeneinander stehen.
 */
const adressfeld = (max) => leerAlsFehlend(z.string().trim().max(max).superRefine((wert, ctx) => {
    const teile = wert.split(/[;,]/).map((a) => a.trim()).filter((a) => a !== "");
    if (teile.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "keine gültige E-Mail-Adresse" });
        return;
    }
    for (const teil of teile) {
        if (!z.string().email().safeParse(teil).success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `keine gültige E-Mail-Adresse: ${teil}`
            });
        }
    }
}).transform((wert) => wert.split(/[;,]/).map((a) => a.trim()).filter((a) => a !== "").join("; ")));
const isoDatum = leerAlsFehlend(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum im Format JJJJ-MM-TT"));
/**
 * Eine Zahl aus einem Eingabefeld.
 *
 * Das Komma ist hier das Wichtigste. Auf dem Telefon zeigt ein Feld mit
 * inputMode="decimal" die Zifferntastatur des Geraets, und die hat auf Deutsch
 * und auf Spanisch ein Komma als Dezimalzeichen. Wer den pH-Wert als "7,2"
 * eintippt, schickte bisher etwas, das Number() zu NaN macht; die Pruefung
 * wies es ab, die Zahl kam nie in der Datenbank an, und auf dem Bildschirm
 * stand sie weiter. Das ist genau die Sorte Fehler, die niemand meldet, weil
 * sie am Objekt gar nicht auffaellt.
 *
 * Umgewandelt wird nur der eindeutige Fall: ein Komma und kein Punkt. Steht
 * beides da, etwa "1.234,50", ist nicht zu entscheiden, was Tausender- und was
 * Dezimalzeichen ist. Dann wird abgewiesen statt geraten, denn eine geratene
 * Zahl ist schlimmer als eine abgewiesene.
 */
const alsZahl = (v) => {
    if (typeof v !== "string")
        return Number(v);
    const roh = v.trim();
    return Number(roh.includes(",") && !roh.includes(".") ? roh.replace(",", ".") : roh);
};
const zahl = (min, max) => z.preprocess((v) => (v === "" || v === null || v === undefined ? undefined : alsZahl(v)), z.number().min(min).max(max).optional());
export const CUSTOMER_KINDS = ["private", "company"];
export const FORMS_OF_ADDRESS = ["du", "sie"];
/**
 * Die Anrede im Brief. "neutral" ist die sichere Vorgabe: daraus wird
 * "Sehr geehrte Damen und Herren", was nie falsch ist. Geraten wird nie.
 */
export const SALUTATIONS = ["herr", "frau", "familie", "neutral"];
export const customerSchema = z.object({
    kind: z.enum(CUSTOMER_KINDS).default("private"),
    displayName: z.string().trim().min(2).max(160),
    companyName: text(160),
    taxId: text(40),
    formOfAddress: z.enum(FORMS_OF_ADDRESS).default("sie"),
    salutation: z.enum(SALUTATIONS).default("neutral"),
    /** Bei Sie der Nachname, bei du der Vorname. */
    salutationName: text(120),
    language: z.enum(CUSTOMER_LOCALES).default("de"),
    // Mehrere Adressen sind zulaessig: ein Haus gehoert oft zweien.
    email: adressfeld(320),
    phone: text(40),
    whatsapp: text(40),
    billingStreet: text(200),
    billingPostalCode: text(20),
    billingCity: text(120),
    billingCountry: text(2),
    notes: text(4000)
});
/** Beim Aendern zaehlt nur, was mitgeschickt wird. Alles andere bleibt stehen. */
export const customerUpdateSchema = customerSchema.partial();
export const propertySchema = z.object({
    customerId: uuidSchema,
    name: z.string().trim().min(2).max(160),
    street: text(200),
    postalCode: text(20),
    city: text(120),
    municipality: text(120),
    country: text(2),
    lat: zahl(-90, 90),
    lng: zahl(-180, 180),
    serviceLevel: text(80),
    contractStart: isoDatum,
    contractEnd: isoDatum,
    accessNote: text(2000),
    notes: text(4000),
    /*
     * Ob am Objekt eine Poolheizung haengt. Steuerte, ob der Eigentuemer im
     * Portal ueberhaupt eine anfordern konnte; seit dessen Wegfall sagt der
     * Haken dem Buero, ob sich zu einer Belegung eine buchen laesst. Ein
     * Kennzeichen am Objekt statt eines Hinweistextes: sonst wird etwas
     * zugesagt, was es nicht gibt.
     */
    poolheizung: z.boolean().optional(),
    /*
     * Ob an diesem Objekt ueberhaupt eine Reinigung buchbar ist. An Haeusern,
     * an denen wir nur Garten und Pool machen, steht der Haken nicht. Bis zu
     * seinem Wegfall bot das Portal den Wunsch dort gar nicht erst an, heute
     * weiss das Buero daran, was der Vertrag deckt.
     */
    reinigungBuchbar: z.boolean().optional()
});
export const propertyUpdateSchema = propertySchema.partial();
// ---------------------------------------------------------------- Baukasten
/**
 * Der Schluessel eines Bereichs.
 *
 * Nur die Form wird hier geprueft. Ob es den Bereich gibt, weiss allein die
 * Datenbank, und sie sagt es ueber den Fremdschluessel; eine zweite Liste im
 * Quelltext waere genau die Doppelpflege, die mit Migration 0053 weggefallen
 * ist.
 */
export const bereichSchema = z.string().trim().regex(BEREICH_SCHLUESSEL, "Ungueltiger Bereich");
/**
 * Einen Bereich anlegen.
 *
 * Getippt wird der Name, den Schluessel bildet der Server daraus. Mitgeben
 * kann man ihn trotzdem, denn beim Einrichten einer Anlage aus einer Vorlage
 * soll derselbe Bereich in zwei Anlagen denselben Schluessel tragen.
 */
export const bereichAnlegenSchema = z.object({
    key: z.string().trim().regex(BEREICH_SCHLUESSEL, "Ungueltiger Schluessel").optional(),
    nameI18n: i18nTextSchema,
    sortOrder: z.number().int().default(0)
});
/** Beim Aendern bleibt der Schluessel fest, Pruefpunkte haengen daran. */
export const bereichUpdateSchema = z.object({
    nameI18n: i18nTextSchema.optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional()
});
/** Die Reihenfolge aller Bereiche in einem Zug, wie bei den Pruefpunkten. */
export const bereichReihenfolgeSchema = z.object({
    ids: z.array(uuidSchema).min(1).max(200)
});
export const INPUT_TYPES = ["status", "number", "boolean", "text", "photo"];
/** Ein Pruefpunkt. Er haengt unmittelbar an einem der Bereiche. */
export const checkpointSchema = z.object({
    /**
     * Der technische Schluessel. Freiwillig.
     *
     * Fehlt er, bildet ihn der Server aus Bereich und deutscher Beschriftung.
     * Bis zum 25.08.2026 war er Pflicht, und das war ein Fehler: wer einen
     * Pruefpunkt anlegt, denkt an "Ueberlauf am Pool gereinigt" und nicht an
     * "pool.ueberlauf". Die Maske hat ihn dafuer abgewiesen.
     */
    key: z.string().trim().regex(/^[a-z0-9]+(\.[a-z0-9_]+)+$/, "Schluessel wie pool.ph").optional(),
    bereich: bereichSchema,
    labelI18n: i18nTextSchema,
    helpI18n: i18nTextSchema.optional(),
    /**
     * Zwischenueberschrift innerhalb des Bereichs, etwa "Aussen" oder
     * "Wasserwerte". Reiner Text ohne eigene Verwaltung: unter Immobilie stehen
     * sonst zwanzig Punkte am Stueck, und niemand findet auf dem Telefon den
     * Uebergang von Sicherheit zu Technik.
     */
    gruppeI18n: i18nTextSchema.optional(),
    inputType: z.enum(INPUT_TYPES),
    unit: text(20),
    targetMin: zahl(-1e9, 1e9),
    targetMax: zahl(-1e9, 1e9),
    sortOrder: z.number().int().default(0),
    photoRequired: z.boolean().default(false)
}).refine((v) => v.targetMin === undefined || v.targetMax === undefined || v.targetMin <= v.targetMax, { message: "Der untere Zielwert darf nicht groesser sein als der obere", path: ["targetMax"] });
/** Beim Aendern bleibt der Schluessel fest, Berichte verweisen darauf. */
export const checkpointUpdateSchema = z.object({
    bereich: bereichSchema.optional(),
    labelI18n: i18nTextSchema.optional(),
    helpI18n: i18nTextSchema.optional(),
    gruppeI18n: i18nTextSchema.optional(),
    inputType: z.enum(INPUT_TYPES).optional(),
    unit: text(20),
    targetMin: zahl(-1e9, 1e9),
    targetMax: zahl(-1e9, 1e9),
    sortOrder: z.number().int().optional(),
    photoRequired: z.boolean().optional(),
    isActive: z.boolean().optional()
});
/**
 * Die Reihenfolge innerhalb eines Bereichs, in einem Zug.
 *
 * Ein Aufruf je verschobenem Punkt waere bei dreissig Punkten dreissig
 * Anfragen und dazwischen ein Zustand, in dem zwei Punkte dieselbe Nummer
 * tragen.
 */
export const reihenfolgeSchema = z.object({
    bereich: bereichSchema,
    ids: z.array(uuidSchema).min(1).max(500)
});
/**
 * Die Checkliste eines Objekts.
 *
 * Sie sagt nur noch, welche Bereiche hier gelten und wie sie heisst. Was
 * darunter gefragt wird, steht zentral am Bereich; was an diesem Haus anders
 * ist, steht als Abweichung am einzelnen Punkt.
 */
export const propertyChecklistSchema = z.object({
    label: text(120),
    bereiche: z.array(bereichSchema).min(1, "Mindestens ein Bereich"),
    intervalDays: z.preprocess((v) => (v === "" || v === null || v === undefined ? undefined : Number(v)), z.number().int().min(1).max(3650).optional())
});
export const propertyChecklistUpdateSchema = propertyChecklistSchema.partial().extend({
    isActive: z.boolean().optional()
});
/**
 * Einzelnen Pruefpunkt am Objekt aufnehmen, abwaehlen oder anpassen.
 * "override" ist der Fall, der die Einarbeitung ersetzt: Zielwert und
 * Objekthinweis stehen dann beim Punkt, wo der Mitarbeiter sie liest.
 */
export const checklistPointOverlaySchema = z.object({
    catalogId: uuidSchema,
    action: z.enum(["add", "remove", "override"]),
    sortOrder: z.number().int().default(0),
    targetMin: zahl(-1e9, 1e9),
    targetMax: zahl(-1e9, 1e9),
    photoRequired: z.boolean().optional(),
    noteI18n: i18nTextSchema.optional()
});
// -------------------------------------------------- Pruefungen und Berichte
export const INSPECTION_STATUSES = [
    "draft", "submitted", "changes_requested", "approved", "sent", "cancelled"
];
/** In Ordnung, Mangel, entfaellt, erledigt. */
export const ITEM_RESULTS = ["ok", "fault", "na", "done"];
export const startInspectionSchema = z.object({
    propertyChecklistId: uuidSchema,
    /**
     * Das Objekt, ueber das berichtet wird.
     *
     * Nur noetig, wenn die gewaehlte Liste eine Berichtsvorlage ist: die haengt
     * an keinem Haus, siehe Migration 0055. Bei einer Objektcheckliste steht das
     * Objekt an der Liste selbst, und diese Angabe wird nicht gebraucht. Fehlt
     * beides und gibt es auch keinen Termin, weist der Server ab, statt sich ein
     * Objekt auszusuchen.
     */
    propertyId: uuidSchema.optional(),
    /**
     * Der Termin, zu dem dieser Bericht gehoert.
     *
     * Optional, denn wer spontan hinfaehrt, hat keinen. Steht er da, gehoert der
     * Bericht dem ganzen Team dieses Termins und nicht dem, der zuerst getippt
     * hat: ein Besuch, ein Rundgang, eine Liste.
     */
    terminId: uuidSchema.optional(),
    /**
     * Schluessel des Geraets. Schickt ein Telefon dieselbe Pruefung nach einem
     * Verbindungsabbruch ein zweites Mal, entsteht kein zweiter Bericht.
     */
    clientRef: text(80)
});
/**
 * Die Unterschrift des Kunden unter dem Bericht.
 *
 * Der Name steht ausdruecklich neben dem Bild. Eine Unterschrift auf einem
 * Telefondisplay ist oft nicht zu entziffern, und ein Nachweis, bei dem
 * niemand mehr sagen kann, wer unterschrieben hat, ist keiner.
 */
export const unterschriftSchema = z.object({
    name: z.string().trim().min(2, "Bitte den Namen in Druckbuchstaben").max(120)
});
export const inspectionItemSchema = z.object({
    result: z.enum(ITEM_RESULTS).optional(),
    valueNumber: zahl(-1e9, 1e9),
    valueText: text(4000),
    valueBool: z.boolean().optional(),
    comment: text(4000),
    commentLang: localeSchema.optional()
});
export const inspectionSummarySchema = z.object({
    summaryLang: z.enum(CUSTOMER_LOCALES),
    summaryText: z.string().trim().max(8000),
    summarySource: z.enum(["ai", "manual", "ai_edited"]).default("manual")
});
/**
 * Die interne Notiz am Bericht.
 *
 * Sie geht nie an den Kunden und steht deshalb nie im PDF. Deshalb ein
 * eigenes Feld und nicht der Kundentext: ein Text, bei dem man beim Schreiben
 * ueberlegen muss, wer ihn spaeter liest, wird nicht geschrieben.
 *
 * Leer heisst geloescht. Das Feld ist der ganze Inhalt, es gibt keinen
 * Verlauf, und eine leere Notiz waere eine Zeile im Reiter ohne Aussage.
 */
export const interneNotizSchema = z.object({
    notiz: z.string().trim().max(4000).nullish()
});
export const reviewSchema = z.object({
    entscheidung: z.enum(["freigeben", "zurueck"]),
    comment: text(4000)
}).refine((v) => v.entscheidung !== "zurueck" || Boolean(v.comment), {
    message: "Bei einer Rueckgabe muss stehen, was fehlt",
    path: ["comment"]
});
/** Fehlerform, die die API einheitlich zurueckgibt. */
export const apiErrorSchema = z.object({
    error: z.string(),
    message: z.string(),
    details: z.unknown().optional()
});
